(() => {
  if (window.yakwaxGalleryLoaded) return;
  window.yakwaxGalleryLoaded = true;
  const desktop = window.matchMedia('(min-width: 799px)');
  const setup = (root = document) => {
    root.querySelectorAll('.yw-product-layout').forEach(layout => {
      if (layout.dataset.ywReady) return;
      layout.dataset.ywReady = 'true';
      const images = layout.querySelector('.product__images');
      const info = layout.querySelector('.product__information');
      const gallery = layout.querySelector('.yw-gallery-main');
      if (!images || !info || !gallery) return;
      let frame;
      const measure = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const header = document.querySelector('#header');
          const announcement = document.querySelector('#announcement-bar');
          const headerHeight = header && getComputedStyle(header).display !== 'none' ? header.getBoundingClientRect().height : 0;
          const announcementHeight = announcement && getComputedStyle(announcement).position === 'sticky' ? announcement.getBoundingClientRect().height : 0;
          const top = Math.max(24, headerHeight + announcementHeight + 16);
          const height = window.innerHeight;
          layout.style.setProperty('--yw-image-top', `${Math.min(top, height - images.offsetHeight - 24)}px`);
          layout.style.setProperty('--yw-info-top', `${Math.min(top, height - info.offsetHeight - 24)}px`);
        });
      };
      const resize = new ResizeObserver(measure);
      resize.observe(images);
      resize.observe(info);
      const header = document.querySelector('#header');
      if (header) resize.observe(header);
      window.addEventListener('resize', measure, { passive: true });
      const thumbnails = Array.from(images.querySelectorAll('.product-gallery__thumbnail'));
      thumbnails.forEach(thumb => thumb.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          thumb.click();
        }
      }));
      const slides = Array.from(gallery.querySelectorAll('.product-gallery__image'));
      const sync = () => {
        const index = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-selected')));
        const counter = images.querySelector('[data-yw-gallery-index]');
        if (counter) counter.textContent = String(index + 1);
        thumbnails.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
        const thumb = thumbnails[index];
        if (!desktop.matches && thumb) {
          const nav = thumb.parentElement;
          if (thumb.offsetLeft < nav.scrollLeft || thumb.offsetLeft + thumb.offsetWidth > nav.scrollLeft + nav.clientWidth) nav.scrollTo({ left: thumb.offsetLeft - 12, behavior: 'instant' });
        }
      };
      const selectedObserver = new MutationObserver(sync);
      slides.forEach(slide => selectedObserver.observe(slide, { attributes: true, attributeFilter: ['class'] }));
      sync();
      measure();
      layout.ywCleanup = () => {
        resize.disconnect();
        selectedObserver.disconnect();
        window.removeEventListener('resize', measure);
        cancelAnimationFrame(frame);
      };
    });
  };
  class YwProductPairings extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.addEventListener('submit', this.add);
      this.addEventListener('click', this.choose);
      this.addEventListener('pointerover', this.prefetch);
      this.addEventListener('focusin', this.prefetch);
      if (this.dataset.performed) return;
      this.controller = new AbortController();
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        fetch(this.dataset.url, { signal: this.controller.signal })
          .then(response => { if (!response.ok) throw new Error('recommendations'); return response.text(); })
          .then(html => {
            const page = new DOMParser().parseFromString(html, 'text/html');
            const result = page.querySelector(`yw-product-pairings[data-product-id="${this.dataset.productId}"][data-performed="true"]`);
            if (result && result.querySelector('.yw-pairing')) this.innerHTML = result.innerHTML;
          })
          .catch(() => {});
      }, { rootMargin: '300px' });
      this.observer.observe(this.parentElement);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.removeEventListener('submit', this.add);
      this.removeEventListener('click', this.choose);
      this.removeEventListener('pointerover', this.prefetch);
      this.removeEventListener('focusin', this.prefetch);
      delete this.dataset.ready;
    }
    // Sizes or colours open the shared options pop-up (assets/yakwax-quick.js); the link stays as the fallback
    choose(event) {
      const trigger = event.target.closest('[data-yw-quick]');
      if (!trigger || !window.ywQuick || typeof HTMLDialogElement !== 'function' || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const card = trigger.closest('.yw-pairing');
      window.ywQuick.open(trigger, {
        onAdded: (variant, summary) => {
          card.querySelector('.yw-pairing__feedback').textContent = summary;
          card.querySelector('.yw-pairing__cart').hidden = false;
        },
      });
    }
    prefetch(event) {
      const trigger = event.target.closest?.('[data-yw-quick]');
      if (trigger && window.ywQuick) window.ywQuick.load(trigger.dataset.ywQuick).catch(() => {});
    }
    async add(event) {
      const form = event.target.closest('.yw-pairing__form');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('button');
      if (button.disabled) return;
      const original = button.innerHTML;
      const card = form.closest('.yw-pairing');
      const status = card.querySelector('.yw-pairing__feedback');
      button.disabled = true;
      button.textContent = form.dataset.adding;
      status.textContent = '';
      try {
        const response = await fetch(form.dataset.addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || form.dataset.error);
        status.textContent = form.dataset.added;
        card.querySelector('.yw-pairing__cart').hidden = false;
        window.PXUTheme?.jsAjaxCart?.updateView();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'yakwax-pairings' } }));
      } catch (error) {
        status.textContent = error.message || form.dataset.error;
      } finally {
        button.disabled = false;
        button.innerHTML = original;
      }
    }
  }
  if (!customElements.get('yw-product-pairings')) customElements.define('yw-product-pairings', YwProductPairings);
  setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.yw-product-layout').forEach(layout => layout.ywCleanup?.()));
})();
